import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { CalendarClock, Church, Copy, CreditCard, Download, Eye, EyeOff, KeyRound, Link2, Loader2, LogOut, Megaphone, ShieldCheck } from "lucide-react";

const PACKAGES = [
  { key: "essential", label: "Essential ($997)" },
  { key: "growth", label: "Growth ($1,497)" },
  { key: "complete", label: "Complete ($2,497)" },
];
const PAY_STAGES = [
  { key: "deposit", label: "50% Deposit" },
  { key: "final", label: "Final 50%" },
];
const fmtMoney = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const STATUSES = [
  { key: "new", label: "New", cls: "bg-gold-500 text-navy-900" },
  { key: "contacted", label: "Contacted", cls: "bg-sky-600 text-white" },
  { key: "won", label: "Won", cls: "bg-emerald-600 text-white" },
];

const fmtSlot = (slot) => {
  if (!slot) return "";
  const [d, hm] = slot.split(" ");
  const [h, m] = hm.split(":").map(Number);
  const h12 = h > 12 ? h - 12 : h;
  const dt = new Date(`${d}T12:00:00`);
  return `${dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · ${h12}:${String(m).padStart(2, "0")} PM ET`;
};

const CSV_COLUMNS = [
  ["Date", (l) => l.created_at], ["Name", (l) => l.name], ["Church", (l) => l.church_name],
  ["Email", (l) => l.email], ["Phone", (l) => l.phone], ["Location", (l) => l.location],
  ["Preferred language", (l) => l.preferred_language], ["Interest", (l) => l.interest],
  ["Message", (l) => l.message], ["Status", (l) => l.status || "new"],
  ["Scheduled call (ET)", (l) => l.booking_slot || ""],
  ["Opted in to public display", (l) => (l.show_church_publicly ? "Yes" : "No")],
  ["Shown on site", (l) => (l.social_proof_approved ? "Yes" : "No")],
];

const csvCell = (v) => {
  let s = String(v ?? "");
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (leads) => {
  const rows = [CSV_COLUMNS.map(([h]) => h).join(",")];
  leads.forEach((l) => rows.push(CSV_COLUMNS.map(([, fn]) => csvCell(fn(l))).join(",")));
  const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `konek-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "konek_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Login = ({ onSuccess }) => {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl border border-navy-100 shadow-xl p-8" data-testid="admin-login-form">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-navy-900 text-gold-500">
          <ShieldCheck size={26} aria-hidden />
        </span>
        <h1 className="mt-5 text-center font-display text-2xl font-semibold text-navy-900">Konek Admin</h1>
        <p className="mt-1.5 text-center text-sm text-charcoal-700/70">Enter your passcode to manage leads.</p>
        <label htmlFor="admin-passcode" className="mt-6 block text-sm font-semibold text-navy-900 mb-1.5">Passcode</label>
        <div className="relative">
          <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal-700/40" aria-hidden />
          <input
            id="admin-passcode"
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            className="w-full bg-[#FAFAFC] border border-navy-100 rounded-xl pl-10 pr-4 py-3 text-charcoal-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            placeholder="••••••••"
            data-testid="admin-passcode-input"
            autoComplete="current-password"
          />
        </div>
        {error && <p className="mt-2 text-sm text-red-600" role="alert" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 text-navy-900 font-semibold px-6 py-3 hover:bg-gold-400 transition-colors disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" aria-hidden /> Signing in…</> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const PaymentLinkMaker = ({ lead, authToken, onCreated }) => {
  const [pkg, setPkg] = useState("essential");
  const [stage, setStage] = useState("deposit");
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState("");

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/payment-links`,
        { lead_id: lead.id, package_key: pkg, stage },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const url = `${window.location.origin}${data.url_path}`;
      setLink(url);
      onCreated();
      toast.success(`Payment link created — ${data.stage_label} · ${fmtMoney(data.amount)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create payment link.");
    } finally {
      setCreating(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied — send it to the pastor.");
  };

  const selCls = "rounded-lg border border-navy-100 bg-white px-2.5 py-1.5 text-xs font-semibold text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500";

  return (
    <div className="mt-3 rounded-xl border border-navy-100 bg-[#FAFAFC] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-charcoal-700/70">
          <CreditCard size={13} aria-hidden /> Payment link:
        </span>
        <select value={pkg} onChange={(e) => setPkg(e.target.value)} className={selCls} data-testid={`paylink-package-${lead.id}`} aria-label="Package">
          {PACKAGES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className={selCls} data-testid={`paylink-stage-${lead.id}`} aria-label="Stage">
          {PAY_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button
          onClick={create}
          disabled={creating}
          data-testid={`paylink-create-${lead.id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-navy-800 transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
        >
          {creating ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Link2 size={13} aria-hidden />} Create
        </button>
      </div>
      {link && (
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg bg-white border border-navy-100 px-2.5 py-1.5 text-[11px] text-charcoal-700" data-testid={`paylink-url-${lead.id}`}>{link}</code>
          <button
            onClick={copy}
            data-testid={`paylink-copy-${lead.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-navy-200 bg-white px-3 py-1.5 text-xs font-bold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            <Copy size={12} aria-hidden /> Copy
          </button>
        </div>
      )}
    </div>
  );
};

const LeadCard = ({ lead, onToggle, onStatus, busy, authToken, onPaymentsChanged }) => (
  <div className="bg-white rounded-2xl border border-navy-100 shadow-sm p-5" data-testid={`lead-card-${lead.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-semibold text-navy-900">{lead.church_name}</p>
        <p className="text-sm text-charcoal-700/80">{lead.name}{lead.location ? ` · ${lead.location}` : ""}</p>
        <p className="mt-1 text-xs text-charcoal-700/60">{fmtDate(lead.created_at)} · {lead.interest}{lead.role ? ` · ${lead.role}` : ""}{lead.preferred_contact ? ` · Prefers: ${lead.preferred_contact}` : ""}</p>
        <p className="mt-1 text-xs text-charcoal-700/60">
          {lead.email && <span>{lead.email}</span>}{lead.email && lead.phone && " · "}{lead.phone && <span>{lead.phone}</span>}
        </p>
        {lead.booking_slot && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-navy-900 text-white text-xs font-semibold px-3 py-1.5" data-testid={`lead-booking-${lead.id}`}>
            <CalendarClock size={13} aria-hidden /> {fmtSlot(lead.booking_slot)}
          </p>
        )}
        {lead.message && <p className="mt-2 text-sm text-charcoal-700 italic">"{lead.message}"</p>}
      </div>
      <div className="flex flex-col items-end gap-2">
        {lead.show_church_publicly ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-500/15 text-gold-600 text-xs font-semibold px-3 py-1">
            <Megaphone size={12} aria-hidden /> Opted in
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-100 text-charcoal-700/70 text-xs font-semibold px-3 py-1">
            Private
          </span>
        )}
        {lead.social_proof_approved && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1" data-testid={`lead-live-${lead.id}`}>
            <Eye size={12} aria-hidden /> Live on site
          </span>
        )}
      </div>
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <div className="inline-flex items-center rounded-full border border-navy-100 bg-[#FAFAFC] p-0.5" role="group" aria-label="Lead status">
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => onStatus(lead, s.key)}
            data-testid={`lead-status-${s.key}-${lead.id}`}
            aria-pressed={(lead.status || "new") === s.key}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${
              (lead.status || "new") === s.key ? s.cls : "text-charcoal-700/60 hover:text-navy-900"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {lead.show_church_publicly && (
        <button
          onClick={() => onToggle(lead)}
          disabled={busy}
          data-testid={`lead-toggle-${lead.id}`}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 ${
            lead.social_proof_approved
              ? "border border-navy-200 bg-white text-navy-900 hover:border-red-400 hover:text-red-600"
              : "bg-navy-900 text-white hover:bg-navy-800"
          }`}
        >
          {busy ? <Loader2 size={15} className="animate-spin" aria-hidden /> : lead.social_proof_approved ? <EyeOff size={15} aria-hidden /> : <Eye size={15} aria-hidden />}
          {lead.social_proof_approved ? "Hide from site" : "Approve & show publicly"}
        </button>
      )}
    </div>
    <PaymentLinkMaker lead={lead} authToken={authToken} onCreated={onPaymentsChanged} />
  </div>
);

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState(null);
  const [payments, setPayments] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
    setPayments([]);
  }, []);

  const fetchPayments = useCallback(async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${tok}` } });
      setPayments(data);
    } catch {
      /* non-fatal */
    }
  }, []);

  const fetchLeads = useCallback(async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${tok}` } });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Could not load leads.");
        setLeads([]);
      }
    }
  }, [logout]);

  useEffect(() => {
    if (token) { fetchLeads(token); fetchPayments(token); }
  }, [token, fetchLeads, fetchPayments]);

  const toggle = async (lead) => {
    setBusyId(lead.id);
    try {
      await axios.patch(
        `${API}/admin/leads/${lead.id}/social-proof`,
        { approved: !lead.social_proof_approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, social_proof_approved: !lead.social_proof_approved } : l)));
      toast.success(!lead.social_proof_approved ? `${lead.church_name} is now shown on the site.` : `${lead.church_name} hidden from the site.`);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(err.response?.data?.detail || "Update failed.");
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (lead, status) => {
    if ((lead.status || "new") === status) return;
    try {
      await axios.patch(
        `${API}/admin/leads/${lead.id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Status update failed.");
    }
  };

  if (!token) {
    return (
      <>
        <Login onSuccess={setToken} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  const optedIn = leads?.filter((l) => l.show_church_publicly).length ?? 0;
  const live = leads?.filter((l) => l.social_proof_approved).length ?? 0;

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <header className="bg-white border-b border-navy-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-900 text-gold-500">
              <Church size={20} aria-hidden />
            </span>
            <div>
              <p className="font-display text-lg font-semibold text-navy-900 leading-tight">Konek Admin</p>
              <p className="text-xs text-charcoal-700/60">Consultation leads &amp; social proof</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
          <button
            onClick={() => {
              if (leads?.length) { downloadCsv(leads); toast.success("CSV downloaded."); }
              else toast("No leads to export yet.");
            }}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            <Download size={15} aria-hidden /> Export CSV
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border border-navy-200 bg-white px-4 py-2 text-sm font-semibold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
          >
            <LogOut size={15} aria-hidden /> Sign out
          </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {leads === null ? (
          <div className="flex items-center justify-center py-24 text-charcoal-700/60">
            <Loader2 size={24} className="animate-spin mr-2" aria-hidden /> Loading leads…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { label: "Total leads", value: leads.length, testid: "stat-total" },
                { label: "Opted in", value: optedIn, testid: "stat-optin" },
                { label: "Live on site", value: live, testid: "stat-live" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl border border-navy-100 p-4 text-center" data-testid={s.testid}>
                  <p className="font-display text-2xl font-semibold text-navy-900">{s.value}</p>
                  <p className="text-xs text-charcoal-700/60 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
            {payments.length > 0 && (
              <div className="mb-8" data-testid="admin-payments-section">
                <h2 className="font-display text-lg font-semibold text-navy-900 mb-3 flex items-center gap-2">
                  <CreditCard size={18} className="text-gold-600" aria-hidden /> Payments
                </h2>
                <div className="space-y-2">
                  {payments.map((p) => (
                    <div key={p.token} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy-100 bg-white px-5 py-3.5" data-testid={`payment-row-${p.token}`}>
                      <div>
                        <p className="font-semibold text-navy-900 text-sm">{p.church_name}</p>
                        <p className="text-xs text-charcoal-700/60">
                          {PACKAGES.find((x) => x.key === p.package_key)?.label || p.package_key} · {PAY_STAGES.find((x) => x.key === p.stage)?.label || p.stage} · {fmtDate(p.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-display font-bold text-navy-900">{fmtMoney(p.amount)}</span>
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`payment-status-${p.token}`}>
                          {p.status === "paid" ? "Paid" : "Unpaid"}
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pay/${p.token}`); toast.success("Link copied."); }}
                          data-testid={`payment-copy-${p.token}`}
                          className="inline-flex items-center gap-1 rounded-full border border-navy-200 bg-white px-3 py-1.5 text-xs font-bold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                        >
                          <Copy size={12} aria-hidden /> Link
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-4" data-testid="admin-leads-list">
              {leads.length === 0 ? (
                <p className="text-center text-charcoal-700/60 py-16">No consultation requests yet.</p>
              ) : (
                leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onToggle={toggle}
                    onStatus={setStatus}
                    busy={busyId === lead.id}
                    authToken={token}
                    onPaymentsChanged={() => fetchPayments(token)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
